import { useState } from "react";
import { createPortal } from "react-dom";
import {
    Key,
    Copy,
    RefreshCw,
    Check,
    X,
    Shield,
    ToggleLeft,
    ToggleRight,
    AlertTriangle,
    Eye,
    EyeOff,
    Loader2
} from "lucide-react";
import { useUniversalKey } from "@/hooks/useUniversalKey";

interface UniversalKeyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function UniversalKeyModal({ isOpen, onClose }: UniversalKeyModalProps) {
    const [activeTab, setActiveTab] = useState<"key" | "providers">("key");
    const {
        keyData,
        loading,
        copied,
        showRegenConfirm,
        setShowRegenConfirm,
        showFullKey,
        setShowFullKey,
        providerLoading,
        isRegenerating,
        isToggling,
        displayKey,
        handleCopy,
        handleRegenerate,
        handleToggle,
        handleProviderToggle,
    } = useUniversalKey(isOpen);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 md:p-8">
            {/* Backdrop with elegant blur */}
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="relative z-10 w-full max-w-lg mx-auto h-auto max-h-[92vh] sm:max-h-[min(85vh,700px)]">
                <div className="relative bg-white dark:bg-card rounded-2xl sm:rounded-2xl shadow-2xl border border-line overflow-hidden h-full flex flex-col">
                    {/* Decorative gradient top */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-sunset flex-none" />

                    {/* Header */}
                    <div className="relative px-4 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 flex-none">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-soft transition-colors text-muted-foreground hover:text-foreground"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex items-center space-x-3 pr-8">
                            <div className="p-2 sm:p-3 rounded-xl bg-ink-blue-900 dark:bg-white/10 text-white flex-shrink-0">
                                <Key className="w-5 h-5 sm:w-6 h-6" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-lg sm:text-xl font-bold text-foreground tracking-tight truncate">
                                    Universal AI Key
                                </h2>
                                <p className="text-[10px] sm:text-sm text-muted-foreground mt-0.5 truncate">
                                    One configured key for all LLM providers
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 mx-4 sm:mx-6 mt-1 p-1 bg-soft rounded-xl border border-line flex-none">
                        <button
                            className={`flex-1 py-1.5 sm:py-2 text-[10px] sm:text-sm font-medium rounded-lg transition-all ${activeTab === "key"
                                ? "bg-white dark:bg-card text-foreground shadow-sm border border-line"
                                : "text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-white/10"
                                }`}
                            onClick={() => setActiveTab("key")}
                        >
                            Key Details
                        </button>
                        <button
                            className={`flex-1 py-1.5 sm:py-2 text-[10px] sm:text-sm font-medium rounded-lg transition-all ${activeTab === "providers"
                                ? "bg-white dark:bg-card text-foreground shadow-sm border border-line"
                                : "text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-white/10"
                                }`}
                            onClick={() => setActiveTab("providers")}
                        >
                            LLM Providers
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-6 min-h-[300px] overflow-y-auto flex-1 h-full">
                        {loading && !keyData && activeTab === 'key' ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 text-pink-500 animate-spin mb-3" />
                                <p className="text-sm text-muted-foreground">Loading Universal AI Key...</p>
                            </div>
                        ) : activeTab === "key" ? (
                            <div className="space-y-5">
                                {/* Key display */}
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold text-foreground">
                                        Your API Key
                                    </h3>
                                    <div className="flex items-center gap-2 p-3 bg-soft rounded-xl border border-line shadow-inner">
                                        <code className="flex-1 text-sm text-foreground font-mono truncate">
                                            {displayKey}
                                        </code>
                                        <button
                                            onClick={() =>
                                                setShowFullKey((v) => !v && !!keyData?.key_value)
                                            }
                                            className="p-1.5 rounded-lg hover:bg-soft text-muted-foreground dark:hover:text-white transition-colors"
                                            title={showFullKey ? "Hide key" : "Show key"}
                                            disabled={!keyData?.key_value}
                                        >
                                            {showFullKey ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                        <button
                                            onClick={handleCopy}
                                            className="p-1.5 rounded-lg hover:bg-soft text-muted-foreground dark:hover:text-white transition-colors"
                                            title="Copy key"
                                        >
                                            {copied ? (
                                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                    {keyData?.is_new && keyData?.key_value && (
                                        <div className="flex items-start space-x-2 mt-2 p-3 rounded-xl bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900/40">
                                            <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                                            <p className="text-xs font-medium text-orange-800 dark:text-orange-300">
                                                Please save this secret key somewhere safe and accessible. For security reasons, you won't be able to view it again through your account. If you lose this secret key, you'll need to generate a new one.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Status Card */}
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold text-foreground">
                                        Key Status
                                    </h3>
                                    <div className="flex items-center justify-between p-4 bg-white dark:bg-white/5 rounded-xl border border-line shadow-sm">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                                                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-foreground">Status</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    Determine if this key can be used
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-xs font-semibold px-3 py-1 rounded-full ${keyData?.is_active
                                                    ? "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-300"
                                                    : "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300"
                                                    }`}
                                            >
                                                {keyData?.is_active ? "Active" : "Disabled"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                                    <button
                                        onClick={handleToggle}
                                        disabled={isToggling}
                                        className={`w-full sm:flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all shadow-sm ${keyData?.is_active
                                            ? "bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/40 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800/40"
                                            : "bg-green-50 dark:bg-green-950/20 hover:bg-green-100 dark:hover:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/40"
                                            }`}
                                    >
                                        {isToggling ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : keyData?.is_active ? (
                                            <ToggleRight className="h-4 w-4" />
                                        ) : (
                                            <ToggleLeft className="h-4 w-4" />
                                        )}
                                        {keyData?.is_active ? "Disable Key" : "Enable Key"}
                                    </button>

                                    {!showRegenConfirm ? (
                                        <button
                                            onClick={() => setShowRegenConfirm(true)}
                                            disabled={isRegenerating}
                                            className="w-full sm:flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-transparent border border-line text-muted-foreground hover:bg-soft transition-all shadow-sm"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                            Regenerate
                                        </button>
                                    ) : (
                                        <div className="w-full sm:flex-1 flex gap-2">
                                            <button
                                                onClick={handleRegenerate}
                                                disabled={isRegenerating}
                                                className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl text-xs sm:text-sm font-medium bg-gradient-sunset text-white hover:opacity-90 transition-all "
                                            >
                                                {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
                                            </button>
                                            <button
                                                onClick={() => setShowRegenConfirm(false)}
                                                className="px-4 py-2.5 rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-transparent border border-line text-muted-foreground hover:bg-soft transition-all"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Timestamps */}
                                {keyData?.last_used_at && (
                                    <p className="text-xs text-muted-foreground text-center pt-2">
                                        Last used:{" "}
                                        <span className="font-medium text-muted-foreground">
                                            {new Date(keyData.last_used_at).toLocaleDateString()}
                                        </span>
                                    </p>
                                )}
                            </div>
                        ) : (
                            /* Providers Tab */
                            <div className="space-y-4">
                                <div className="space-y-1 mb-6">
                                    <h3 className="text-sm font-semibold text-foreground">
                                        Configure Providers
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Select which AI providers this universal AI key can access. Any requests made to disabled providers will be rejected automatically.
                                    </p>
                                </div>

                                {/* OpenAI */}
                                <div className="flex items-center justify-between p-4 bg-white dark:bg-white/5 border border-line rounded-xl shadow-sm hover:border-input transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-950/30 flex items-center justify-center border border-green-100 dark:border-green-800/40 flex-shrink-0">
                                            <span className="text-xl font-bold text-green-600 dark:text-green-400">
                                                G
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-base font-semibold text-foreground">GPT Models</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                OpenAI's advanced reasoning models
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleProviderToggle(
                                                "openai",
                                                !keyData?.provider_openai
                                            )
                                        }
                                        disabled={providerLoading === "openai"}
                                        className="relative p-2"
                                    >
                                        {providerLoading === "openai" ? (
                                            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                                        ) : (
                                            <div
                                                className={`w-12 h-6 rounded-full transition-all duration-300 ${keyData?.provider_openai
                                                    ? "bg-gradient-sunset"
                                                    : "bg-line"
                                                    }`}
                                            >
                                                <div
                                                    className={`absolute top-[10px] w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${keyData?.provider_openai
                                                        ? "left-[26px]"
                                                        : "left-[10px]"
                                                        }`}
                                                />
                                            </div>
                                        )}
                                    </button>
                                </div>

                                {/* Anthropic */}
                                <div className="flex items-center justify-between p-4 bg-white dark:bg-white/5 border border-line rounded-xl shadow-sm hover:border-input transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center border border-orange-100 dark:border-orange-900/40 flex-shrink-0">
                                            <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                                                C
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-base font-semibold text-foreground">Claude Models</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                Anthropic's advanced reasoning models
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            handleProviderToggle(
                                                "anthropic",
                                                !keyData?.provider_anthropic
                                            )
                                        }
                                        disabled={providerLoading === "anthropic"}
                                        className="relative p-2"
                                    >
                                        {providerLoading === "anthropic" ? (
                                            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                                        ) : (
                                            <div
                                                className={`w-12 h-6 rounded-full transition-all duration-300 ${keyData?.provider_anthropic
                                                    ? "bg-gradient-sunset"
                                                    : "bg-line"
                                                    }`}
                                            >
                                                <div
                                                    className={`absolute top-[10px] w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${keyData?.provider_anthropic
                                                        ? "left-[26px]"
                                                        : "left-[10px]"
                                                        }`}
                                                />
                                            </div>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
